/**
 * Виджет AI-ассистента площадки (ADR 0020, V9.5).
 *
 * Загружается ЛЕНИВО и только когда площадке включена надстройка
 * (/v/{venue}/assistant.json): конфиг спрашиваем после карты (не в
 * критическом пути «QR → карта за 2 с»), сам скрипт виджета — в idle-паузу.
 * Ключ виджета публичный и домен-привязанный: с чужого origin бесполезен.
 *
 * ЧС: во время аварийного режима ассистент СКРЫТ (CSS-правило по классу на
 * <html>, см. emergency.css) — в панике единственный канал это карта с
 * маршрутом к выходу, чат-пузырь рядом с ней был бы вредным отвлечением.
 * Правило работает и для виджета, появившегося ПОЗЖЕ включения ЧС: CSS
 * не зависит от порядка событий, в отличие от прямой правки style.
 *
 * Язык виджет берёт из <html lang> — main.tsx уже ведёт его при переключении.
 */
import { effect } from '@preact/signals'
import { scheduleIdle } from './idle'
import { assistantOn, emergencyFrame, emergencyHazard } from './state'

interface AssistantConfig {
  readonly enabled: boolean
  readonly key?: string
  readonly src?: string
}

export const EMERGENCY_HTML_CLASS = 'emergency-active'

let injected = false

/** Скрытие ассистента в ЧС — класс на <html> ведём всегда (даже если виджет
 *  ещё не загружен): он же пригоден любым будущим «не в ЧС» украшениям. */
export function watchEmergencyClass(): void {
  effect(() => {
    const emergency = emergencyFrame.value !== null || emergencyHazard.value !== null
    document.documentElement.classList.toggle(EMERGENCY_HTML_CLASS, emergency)
  })
}

/** Спросить конфиг и, если ассистент включён площадке, поставить виджет. */
export async function loadAssistant(venueId: string): Promise<void> {
  if (injected) return
  try {
    const response = await fetch(`/v/${encodeURIComponent(venueId)}/assistant.json`)
    if (!response.ok) return
    const cfg = (await response.json()) as AssistantConfig
    if (!cfg.enabled || !cfg.key || !cfg.src) return
    // Интерфейс узнаёт про ИИ отсюда: пилюля подсказок и карточка ответа в
    // поиске появляются только у площадок с подключённым ассистентом.
    assistantOn.value = true
    scheduleIdle(() => injectWidget(cfg))
  } catch {
    // Сервер молчит/оффлайн: карта полностью работоспособна без ассистента.
  }
}

function injectWidget(cfg: AssistantConfig): void {
  if (injected || !cfg.key || !cfg.src) return
  injected = true
  const script = document.createElement('script')
  script.src = cfg.src
  script.defer = true
  script.dataset['key'] = cfg.key
  // Автостиль виджета: акцент и тема подхватываются с нашей страницы.
  script.dataset['accent'] = 'auto'
  script.dataset['mode'] = 'auto'
  document.body.appendChild(script)
}
